// src/components/profiles/shared/navigation/IconBar.tsx

"use client";

import React, { useEffect, useMemo, useState } from "react";
import type { RootState, AppDispatch } from "../../../../redux/storeTypes";
import { useDispatch, useSelector } from "react-redux";
import { useRouter, usePathname } from "next/navigation";
import Link from "next/link";

import { fetchUnreadCounts } from "../../../../redux/conversation/conversationSlice";
import { logoutUser } from "../../../../redux/auth/authSlice";
import { getMemberProfile } from "../../../../redux/profiles/profileSlice";

import { selectAuthUser } from "../../../../redux/selectors/profile/userSelectors";

import {
  selectRemainingSecondsForScope,
  selectScopeAccess,
} from "../../../../redux/litshield/litshieldSelectors";

import {
  fetchScopeAccess,
  logoutAllLITShieldScopes,
} from "../../../../redux/litshield/litshieldSlice";

import { selectTotalUnreadCount } from "@/redux/selectors/conversation/conversationSelectors";

import CreateDrawer from "../../../create/CreateDrawer";
import { AvatarDisplay } from "../../../common";
import { IconWithBadge } from "../../../common/index";
import { CrossIcon } from "../../../icons/index";

import {
  Earth,
  Goal,
  CircleFadingPlus,
  MessageCircleMore,
  Settings,
  LogOut,
} from "lucide-react";

import { useNotifications } from "../../../../hooks/notifications/useNotifications";
import NotificationDrawer from "../../../../components/notifications/NotificationDrawer";

import { isOwnerProfileTemporarilyUnavailable } from "../../../../utils/profiles/profileGate";
import { logger } from "../../../../utils/logging/logger";

import "./IconBar.css";

interface NavItem {
  href: string;
  icon: JSX.Element;
  label: string;
  title?: string;
  disabled?: boolean;
  disabledTitle?: string;
}

const isMemberLikeProfile = (profile: any): boolean => {
  if (!profile) return false;

  if (profile?.user?.is_member === true) return true;

  if ("is_hidden_by_confidants" in profile) return true;
  if ("is_townlit_verified" in profile) return true;
  if ("townlit_verified_at" in profile) return true;
  if ("profile_gate" in profile && profile?.user?.is_member !== false)
    return true;

  return false;
};

const IconBar: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const router = useRouter();
  const pathname = usePathname();

  const [openNotif, setOpenNotif] = useState(false);
  const [openCreate, setOpenCreate] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const user = useSelector(selectAuthUser) as any;

  // Read the raw profile from the slice.
  // Do not use selectMemberProfile here because limited owner payload may not match full member shape.
  const rawProfile = useSelector(
    (state: RootState) => state.profile.profile,
  ) as any;
  const profileLoading = useSelector(
    (state: RootState) => state.profile.loading,
  );

  const memberProfile = useMemo(() => {
    if (!isMemberLikeProfile(rawProfile)) return null;
    return rawProfile;
  }, [rawProfile]);

  const totalUnread = useSelector(selectTotalUnreadCount);

  const token =
    typeof window !== "undefined" ? localStorage.getItem("access_token") : null;

  const conversationAccess = useSelector(selectScopeAccess("conversation"));
  const covenantAccess = useSelector(selectScopeAccess("covenant"));
  const hasAccess = conversationAccess || covenantAccess;

  const remainingConversation = useSelector(
    selectRemainingSecondsForScope("conversation"),
  );

  const remainingCovenant = useSelector(
    selectRemainingSecondsForScope("covenant"),
  );

  const remainingTime = remainingConversation || remainingCovenant;

  const isMemberUser =
    user?.is_member === true || localStorage.getItem("is_member") === "true";

  const isRestrictedOwnerProfile =
    isMemberUser &&
    !!memberProfile &&
    isOwnerProfileTemporarilyUnavailable(memberProfile);

  const notificationToken = isRestrictedOwnerProfile ? null : token;
  const { unread } = useNotifications(notificationToken);

  const avatarSrc = user?.avatar_cdn_url || null;
  const placeholderSrc = user?.avatar_url || user?.profile_image || null;

  useEffect(() => {
    if (isRestrictedOwnerProfile) return;

    dispatch(fetchUnreadCounts());
  }, [dispatch, isRestrictedOwnerProfile]);

  useEffect(() => {
    if (isRestrictedOwnerProfile) return;

    const scopes = ["conversation", "covenant"];

    scopes.forEach((scope) => {
      dispatch(fetchScopeAccess(scope));
    });
  }, [dispatch, isRestrictedOwnerProfile]);

  // Ensure IconBar has the owner member profile even if ProfilePage used a local fallback.
  useEffect(() => {
    if (!user || user.is_deleted || user.is_suspended) return;
    if (!isMemberUser) return;
    if (profileLoading) return;

    if (!memberProfile) {
      dispatch(getMemberProfile()).catch(() => {
        // Keep icon bar resilient.
      });
    }
  }, [
    dispatch,
    user?.id,
    user?.is_deleted,
    user?.is_suspended,
    isMemberUser,
    profileLoading,
    memberProfile,
  ]);

  if (!user || user.is_deleted || user.is_suspended) {
    return null;
  }

  const formatTime = (seconds: number) => {
    const min = Math.floor(seconds / 60);
    const sec = seconds % 60;

    return `${min.toString().padStart(2, "0")}:${sec
      .toString()
      .padStart(2, "0")}`;
  };

  const handleLogout = async () => {
    if (isLoggingOut) return;

    setIsLoggingOut(true);

    try {
      const result = await dispatch(logoutUser());

      if (logoutUser.fulfilled.match(result)) {
        router.replace("/");
        return;
      }

      setIsLoggingOut(false);
    } catch (error: any) {
      logger.error("Logout failed from restricted icon bar", {
        error: error?.message,
      });

      setIsLoggingOut(false);
    }
  };

  const handleQuickLogout = async () => {
    try {
      dispatch(logoutAllLITShieldScopes());

      if (
        pathname.startsWith("/conversation") ||
        pathname.startsWith("/settings/lit-covenant")
      ) {
        router.push("/");
      }
    } catch (err: any) {
      logger.error("Quick logout failed", { error: err?.message });
    }
  };

  const profileAvatarItem = (
    <Link href="/" prefetch={false} aria-label="Open profile">
      <div
        className={`icon-bar-item ${pathname === "/" ? "active" : ""}`}
        title="Your TownLIT profile"
      >
        <AvatarDisplay
          userId={user.id}
          src={avatarSrc}
          type="user"
          placeholderSrc={placeholderSrc}
          alt={user.name || user.username}
          shape="circle"
          size="xs"
          withBorder
          borderColor={user.label?.color}
          title="Your profile"
        />
      </div>
    </Link>
  );

  if (isRestrictedOwnerProfile) {
    return (
      <nav
        className="icon-bar icon-bar--restricted"
        aria-label="Restricted profile navigation"
      >
        {profileAvatarItem}

        <button
          type="button"
          className="icon-bar-item icon-bar-item--logout"
          title="Log out"
          aria-label="Log out"
          onClick={handleLogout}
          disabled={isLoggingOut}
        >
          <LogOut size={25} />
        </button>
      </nav>
    );
  }

  const navItems: NavItem[] = [
    {
      href: "/",
      icon: (
        <AvatarDisplay
          userId={user.id}
          src={avatarSrc}
          type="user"
          placeholderSrc={placeholderSrc}
          alt={user.name || user.username}
          shape="circle"
          size="xs"
          withBorder
          borderColor={user.label?.color}
          title="Your profile"
        />
      ),
      label: "Corner",
      title: "Your spiritual corner in the TownLIT",
      disabled: false,
    },
    {
      href: "/square",
      icon: <Earth size={26} />,
      label: "Square",
      title: "TownLIT Square – Explore public faith moments",
      disabled: false,
    },
    {
      href: "#",
      icon: <Goal size={26} />,
      label: "Notification",
      title: "See your recent notifications",
      disabled: false,
    },
    {
      href: "#",
      icon: <CircleFadingPlus size={26} />,
      label: "Add New",
      title: "Create something new",
      disabled: false,
    },
    {
      href: "/conversation",
      icon: <MessageCircleMore size={26} />,
      label: "Messenger",
      title: "TownLIT Messenger – Private, secure, and yours",
    },
    {
      href: "/settings",
      icon: <Settings size={26} />,
      label: "Settings",
      title: "Manage your account and preferences",
    },
  ];

  const isActiveRoute = (href: string) => {
    if (!href || href === "#") return false;

    if (href === "/") {
      return pathname === "/";
    }

    return pathname.startsWith(href);
  };

  return (
    <>
      <nav className="icon-bar" aria-label="TownLIT navigation">
        {navItems.map((item, index) => {
          const isMessenger = item.label === "Messenger";
          const isNotification = item.label === "Notification";
          const isAddNew = item.label === "Add New";

          if (item.disabled) {
            return (
              <div
                key={`${item.label}-${index}`}
                className="icon-bar-item disabled"
                title={item.disabledTitle || "Coming soon"}
                aria-disabled="true"
                role="link"
                tabIndex={-1}
              >
                {item.icon}
              </div>
            );
          }

          if (isNotification) {
            return (
              <button
                key={`${item.label}-${index}`}
                className="icon-bar-item"
                title={item.title || item.label}
                onClick={() => setOpenNotif(true)}
                aria-label="Open notifications"
                type="button"
              >
                <IconWithBadge
                  icon={item.icon}
                  count={unread}
                  title={item.title}
                  badgeColor="var(--color-primary)"
                />
              </button>
            );
          }

          if (isAddNew) {
            return (
              <button
                key={`${item.label}-${index}`}
                className={`icon-bar-item ${openCreate ? "active" : ""}`}
                title={item.title || item.label}
                onClick={() => setOpenCreate(true)}
                aria-label="Open create drawer"
                type="button"
              >
                {item.icon}
              </button>
            );
          }

          return (
            <Link
              key={`${item.label}-${index}`}
              href={item.href}
              prefetch={false}
            >
              <div
                className={`icon-bar-item ${
                  isActiveRoute(item.href) ? "active" : ""
                }`}
                title={item.title || item.label}
              >
                {isMessenger ? (
                  <IconWithBadge
                    icon={item.icon}
                    count={totalUnread}
                    title={item.title}
                    badgeColor="var(--color-primary)"
                  />
                ) : (
                  item.icon
                )}
              </div>
            </Link>
          );
        })}

        {hasAccess && (
          <button
            onClick={handleQuickLogout}
            className="icon-bar-item"
            title={
              remainingTime
                ? `LITShield – Access expires in ${formatTime(remainingTime)}`
                : "LITShield – Leave safely"
            }
            type="button"
          >
            <div className="cross-icone">
              <CrossIcon />
            </div>
          </button>
        )}
      </nav>

      <NotificationDrawer
        open={openNotif}
        onClose={() => setOpenNotif(false)}
        token={token}
      />

      <CreateDrawer open={openCreate} onClose={() => setOpenCreate(false)} />
    </>
  );
};

export default IconBar;
